// webrtc-ip-probe.js
// 用法：import { probeStunServers, runProbeInBackground } from './webrtc-ip-probe.js'
// 返回值格式 { perServer: {stunUrl: [candidateObjs...]}, merged: ['1.2.3.4'], raw: allResults }

const IPV4_RE = /((25[0-5]|2[0-4]\d|1?\d{1,2})(\.(25[0-5]|2[0-4]\d|1?\d{1,2})){3})/;
function parseCandidateText(text){
    const m = text.match(IPV4_RE);
    if(!m) return null;
    const ip = m[1];
    const typ = (text.match(/typ\s+(\S+)/i) || [])[1] || '';
    const mdns = /\.local\b/i.test(text);
    return { ip, typ, raw: text, mdns };
}
function isPrivateIPv4(ip){
    const p = ip.split('.').map(Number);
    return (p[0] === 10) || (p[0] === 127) || (p[0] === 192 && p[1] === 168) || (p[0] === 172 && p[1] >= 16 && p[1] <= 31);
}
function filterPublic(list){
    const s = new Set(), out = [];
    for(const c of list){
        if(!c || !c.ip) continue;
        if(c.mdns || isPrivateIPv4(c.ip)) continue;
        if(!s.has(c.ip)){ s.add(c.ip); out.push(c.ip); }
    }
    return out;
}

async function probeOnce(stunUrl, timeoutMs = 4000){
    // 返回 candidate 对象数组（解析过的）
    const pc = new RTCPeerConnection({ iceServers: [{ urls: stunUrl }] });
    try { pc.createDataChannel('probe'); } catch(e){}
    const candidates = [];
    let finished = false;

    return new Promise(resolve => {
        function cleanup(){
            try{ pc.close(); }catch{}
            finished = true;
        }
        pc.onicecandidate = (e) => {
            if(e.candidate && e.candidate.candidate){
                const parsed = parseCandidateText(e.candidate.candidate);
                if(parsed) candidates.push(parsed);
            } else {
                // null candidate -> end of gathering
                if(!finished){ cleanup(); resolve(candidates); }
            }
        };
        pc.createOffer().then(o => pc.setLocalDescription(o)).catch(()=>{});
        // timeout fallback
        setTimeout(()=>{ if(!finished){ cleanup(); resolve(candidates); } }, timeoutMs);
    });
}

/**
 * 主探测函数
 * @param {Object} opts
 * @param {string[]} opts.stunUrls - e.g. ['stun:stun.jidussy.cn:3478', 'stun:stun.l.google.com:19302']
 * @param {number} opts.timeoutMs - 单个 STUN 超时时间，默认 4000ms
 * @param {(progress)=>void} opts.onProgress - 可选回调，接收 { stage: 'start'|'perServer'|'done', url?, data? }
 * @returns {Promise<{ perServer: Object, merged: string[], raw: any }>}
 */
export async function probeStunServers({ stunUrls = ['stun:stun.jidussy.cn:3478','stun:stun.l.google.com:19302'], timeoutMs = 4000, onProgress } = {}){
    if(onProgress) onProgress({ stage: 'start', stunUrls });
    const promises = stunUrls.map(url => probeOnce(url, timeoutMs).then(c => ({ url, c })));
    const all = await Promise.all(promises);
    if(onProgress) onProgress({ stage: 'perServer', data: all });

    const perServer = {};
    for(const item of all){
        perServer[item.url] = (item.c || []).map(x=>x); // keep parsed candidate objects
    }

    // 合并并只保留公网 IPv4（去重）
    const merged = [];
    const seen = new Set();
    for(const url of stunUrls){
        const list = perServer[url] || [];
        const pubIps = filterPublic(list);
        for(const ip of pubIps) if(!seen.has(ip)){ seen.add(ip); merged.push(ip); }
    }

    if(onProgress) onProgress({ stage: 'done', merged, perServer, raw: all });
    return { perServer, merged, raw: all };
}

/**
 * 在后台（但仍在主线程）触发探测，尽量不阻塞首屏/交互。
 * 会使用 requestIdleCallback（有则用），否则 setTimeout 作为后备。
 * @param opts - 同 probeStunServers 的参数
 * @returns Promise 从后台返回探测结果（不会阻塞 UI，因为任务延后执行）
 */
export function runProbeInBackground(opts = {}){
    return new Promise((resolve, reject) => {
        const run = async () => {
            try{
                const res = await probeStunServers(opts);
                resolve(res);
            }catch(e){ reject(e); }
        };
        if('requestIdleCallback' in window){
            requestIdleCallback(() => { run(); }, { timeout: 2000 });
        }else{
            // 微任务延后（保证页面首次渲染优先）
            setTimeout(() => { run(); }, 50);
        }
    });
}

/**
 * 若希望把探测完全移到 Worker（避免任何主线程影响），可以把下面的文本写入 worker 文件并在主线程通过 postMessage 使用。
 * 下面是一个最小的 Worker 消息约定示例（这里只是示例字符串，未暴露为函数）
 *
 * Worker 消息约定:
 * - 主线程 -> worker: { type: 'start', stunUrls: [...], timeoutMs: 4000 }
 * - worker -> 主线程: { type: 'progress', stage: 'start'|'perServer'|'done', data: ... }
 *
 * 注意：Worker 里依然可以使用 RTCPeerConnection（大多数现代浏览器支持在 worker 中使用 WebRTC），但兼容性需验证。
 */
export default {
    probeStunServers,
    runProbeInBackground,
};
